import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Heart,
  Users,
  DollarSign,
  TrendingUp,
  PlusCircle,
  Eye,
  MessageCircle,
  Download,
  Settings,
  Bell,
  BadgeCheck,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  FileText,
  Shield,
  Wallet,
  Calendar,
  Share2,
  Edit,
  MoreVertical,
  Loader2,
  Mail,
  Phone,
  FileCheck,
  AlertTriangle,
  Star,
  Target,
  Flag,
  CheckCircle2,
  Circle,
  Upload,
  CreditCard,
  Rocket,
  Filter,
  Pause,
  Play,
  Trash2,
  RotateCcw,
  CheckSquare,
  Square,
  Send,
  Reply,
  ExternalLink,
  Gift,
  Search,
  X,
  ArrowUpDown,
  SortAsc,
  SortDesc,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { campaignAPI, donationAPI } from '../services/api';
import apiClient from '../services/api';
import UserDisputesSection from '../components/UserDisputesSection';
import UserInvoicesSection from '../components/billing/UserInvoicesSection';
import { myCampaigns as mockCampaigns, donationHistory as mockDonations, notifications as mockNotifications } from '../data/mock';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import StripeConnectSetup from '../components/payment/StripeConnectSetup';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import CreatorTeamPagesSection from '../components/dashboard/CreatorTeamPagesSection';
import MyTeamPagesSection from '../components/dashboard/MyTeamPagesSection';
import ReferralDashboardSection from '../components/referral/ReferralDashboardSection';
import { REFERRALS_ENABLED } from '../utils/featureFlags';
import RecommendationStatsCard from '../components/recommendations/RecommendationStatsCard';

const DashboardPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial dashboard mode and tab from URL
  const initialMode = searchParams.get('mode') || 'creator';
  const requestedTab = searchParams.get('tab') || 'all';
  // Block direct ?tab=referrals navigation when the feature is disabled
  const initialTab = (!REFERRALS_ENABLED && requestedTab === 'referrals') ? 'all' : requestedTab;
  const [dashboardMode, setDashboardMode] = useState(initialMode); // 'creator' or 'donor'
  const [activeTab, setActiveTab] = useState(initialTab);
  const [myCampaigns, setMyCampaigns] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [inactiveCampaigns, setInactiveCampaigns] = useState([]);
  const [deletedCampaigns, setDeletedCampaigns] = useState([]);
  const [draftCampaigns, setDraftCampaigns] = useState([]);
  const [projectCampaigns, setProjectCampaigns] = useState([]);
  const [myDonations, setMyDonations] = useState([]);
  const [donationsReceived, setDonationsReceived] = useState({ donations: [], total_received: 0, total_amount: 0, unique_donors: 0 });
  const [backedProjects, setBackedProjects] = useState({ backed_projects: [], total_projects_backed: 0, total_amount_donated: 0 });
  const [contactMessages, setContactMessages] = useState([]);
  const [myConversations, setMyConversations] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'campaign', 'project'
  const [searchQuery, setSearchQuery] = useState(''); // Search by keyword
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'most_raised', 'least_raised', 'most_donors'
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month', '3months', 'year'
  const [verificationStatus, setVerificationStatus] = useState({
    email_verified: false,
    phone_verified: false,
    kyc_status: 'not_submitted',
  });

  // Selection states for bulk actions
  const [selectedActive, setSelectedActive] = useState([]);
  const [selectedInactive, setSelectedInactive] = useState([]);
  const [selectedDeleted, setSelectedDeleted] = useState([]);
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Subscriptions state
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(null);
  
  // Milestone proof upload states
  const [showProofUploadDialog, setShowProofUploadDialog] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofDescription, setProofDescription] = useState('');
  const [uploadingProof, setUploadingProof] = useState(false);
  
  // Collaboration invitations state
  const [collaborationInvites, setCollaborationInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [respondingToInvite, setRespondingToInvite] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Handle proof upload
  const handleUploadProof = async () => {
    if (!selectedMilestone || !proofFile) return;
    
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      formData.append('description', proofDescription);
      formData.append('milestone_id', selectedMilestone.id || selectedMilestone.name);
      formData.append('campaign_id', selectedMilestone.campaign_id);
      
      await apiClient.post('/campaigns/milestone/proof', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Refresh campaigns
      await fetchMyCampaigns();
      setShowProofUploadDialog(false);
      setProofFile(null);
      setProofDescription('');
      setSelectedMilestone(null);
      alert('Proof uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload proof:', error);
      alert(error.response?.data?.detail || 'Failed to upload proof. Please try again.');
    } finally {
      setUploadingProof(false);
    }
  };

  // Bulk action handler
  const handleBulkAction = async (action, campaignIds, clearSelection) => {
    if (!campaignIds.length) return;
    
    const actionMessages = {
      delete: 'delete',
      pause: 'pause',
      resume: 'resume',
      restore: 'restore',
      permanent_delete: 'permanently delete'
    };
    
    if (!window.confirm(`Are you sure you want to ${actionMessages[action]} ${campaignIds.length} item(s)?`)) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      const response = await apiClient.post('/campaigns/bulk-action', {
        campaign_ids: campaignIds,
        action: action
      });
      console.log('Bulk action response:', response.data);
      clearSelection([]);
      // Refresh data by re-fetching current tab data
      await fetchTabData(activeTab);
      // Also refresh main campaign list
      try {
        const campaigns = await campaignAPI.getMyCampaigns({ exclude_deleted: true, exclude_drafts: true, limit: 100 });
        setMyCampaigns(campaigns.map(mapCampaign));
      } catch (e) {
        console.error('Failed to refresh campaigns:', e);
      }
    } catch (err) {
      console.error('Bulk action failed:', err);
      alert(`Failed to perform action: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete notifications
  const handleBulkDeleteNotifications = async () => {
    if (!selectedNotifications.length) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedNotifications.length} notification(s)?`)) {
      return;
    }
    
    setBulkActionLoading(true);
    try {
      await apiClient.post('/notifications/bulk-delete', {
        notification_ids: selectedNotifications
      });
      setSelectedNotifications([]);
      // Refresh notifications
      const response = await apiClient.get('/notifications/', { params: { limit: 20 } });
      setNotifications(response.data.notifications || []);
    } catch (err) {
      console.error('Bulk delete notifications failed:', err);
      alert('Failed to delete notifications. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Fetch notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await apiClient.get('/notifications/', {
          params: { limit: 20 }
        });
        setNotifications(response.data.notifications || []);
      } catch (err) {
        console.log('Failed to fetch notifications:', err);
        setNotifications([]);
      }
    };
    if (user) fetchNotifications();
  }, [user]);

  const markNotificationRead = async (notificationId) => {
    // Update locally immediately
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
    // Persist to database
    try {
      await apiClient.post(`/notifications/mark-read/${notificationId}`);
    } catch (err) {
      console.log('Failed to mark notification read:', err);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      // Persist to database first
      await apiClient.post('/notifications/mark-all-read');
      // Then update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      alert('Failed to mark notifications as read. Please try again.');
    }
  };

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showNotifications && !e.target.closest('.notification-dropdown')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showNotifications]);

  // Fetch data based on active tab
  const fetchTabData = async (tab) => {
    setTabLoading(true);
    try {
      let data = [];
      switch (tab) {
        case 'all':
          // Fetch ALL campaigns from the platform (not just user's campaigns)
          try {
            const allCampaignsRes = await apiClient.get('/campaigns?exclude_deleted=true&exclude_drafts=true');
            const allPlatformCampaigns = (allCampaignsRes.data || []).map(mapCampaign);
            setActiveCampaigns(allPlatformCampaigns.filter(c => c.status === 'active'));
            setInactiveCampaigns(allPlatformCampaigns.filter(c => c.status === 'inactive'));
          } catch (e) {
            console.error('Failed to fetch all campaigns:', e);
          }
          break;
        case 'active':
          // Fetch active campaigns directly from API to avoid race condition with myCampaigns
          try {
            const activeRes = await apiClient.get('/campaigns?creator=me&campaign_status=active');
            setActiveCampaigns((activeRes.data || []).map(mapCampaign));
          } catch (e) {
            console.error('Failed to fetch active campaigns:', e);
            // Fallback to filtering myCampaigns if API fails
            const activeItems = myCampaigns.filter(c => 
              c.status === 'active'
            );
            setActiveCampaigns(activeItems);
          }
          break;
        case 'inactive':
          // Fetch paused/inactive campaigns
          const inactiveRes = await apiClient.get('/campaigns?creator=me&campaign_status=inactive');
          setInactiveCampaigns((inactiveRes.data || []).map(mapCampaign));
          break;
        case 'deleted':
          // Fetch deleted campaigns
          const deletedRes = await apiClient.get('/campaigns?creator=me&campaign_status=deleted');
          setDeletedCampaigns((deletedRes.data || []).map(mapCampaign));
          break;
        case 'drafts':
          try {
            data = await campaignAPI.getMyDrafts();
            console.log('Drafts fetched:', data);
            setDraftCampaigns((data || []).map(mapCampaign));
          } catch (draftErr) {
            console.error('Error fetching drafts:', draftErr);
            setDraftCampaigns([]);
          }
          break;
        case 'projects':
          data = await campaignAPI.getMyProjects();
          setProjectCampaigns(data.map(mapCampaign));
          break;
        case 'backed':
          const backedRes = await apiClient.get('/donations/backed-projects');
          setBackedProjects(backedRes.data);
          break;
        case 'received':
          const receivedRes = await apiClient.get('/donations/received');
          setDonationsReceived(receivedRes.data);
          break;
        case 'messages':
          const messagesRes = await apiClient.get(`/contact/inbox?user_id=${user?.id}`);
          setContactMessages(messagesRes.data.messages || []);
          break;
        case 'my-messages':
          const myMsgsRes = await apiClient.get(`/contact/my-conversations?user_id=${user?.id}&email=${user?.email}`);
          setMyConversations(myMsgsRes.data.conversations || []);
          break;
        case 'team-pages':
          // Load active campaigns for team fundraising management
          const teamActiveRes = await apiClient.get('/campaigns?creator=me&campaign_status=active');
          setActiveCampaigns((teamActiveRes.data || []).map(mapCampaign));
          break;
        default:
          break;
      }
    } catch (e) {
      console.error(`Failed to fetch ${tab} data:`, e);
    } finally {
      setTabLoading(false);
    }
  };

  // Campaign action handlers
  const handlePauseCampaign = async (campaignId) => {
    setActionLoading(prev => ({ ...prev, [campaignId]: 'pause' }));
    try {
      await apiClient.post(`/campaigns/${campaignId}/pause`);
      // Move campaign to inactive list
      const campaign = myCampaigns.find(c => c.id === campaignId) || activeCampaigns.find(c => c.id === campaignId);
      if (campaign) {
        campaign.status = 'inactive';
        setInactiveCampaigns(prev => [...prev, campaign]);
      }
      setMyCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, status: 'inactive' } : c));
      setActiveCampaigns(prev => prev.filter(c => c.id !== campaignId));
    } catch (err) {
      console.error('Failed to pause campaign:', err);
      alert('Failed to pause campaign. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: null }));
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    setActionLoading(prev => ({ ...prev, [campaignId]: 'resume' }));
    try {
      await apiClient.post(`/campaigns/${campaignId}/resume`);
      // Move campaign back to active
      setInactiveCampaigns(prev => prev.filter(c => c.id !== campaignId));
      setMyCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, status: 'active' } : c));
      setActiveCampaigns(prev => {
        const campaign = myCampaigns.find(c => c.id === campaignId) || inactiveCampaigns.find(c => c.id === campaignId);
        if (campaign) {
          return [...prev, { ...campaign, status: 'active' }];
        }
        return prev;
      });
    } catch (err) {
      console.error('Failed to resume campaign:', err);
      alert('Failed to resume campaign. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: null }));
    }
  };

  // Handle replying to a contact message
  const handleReplyToMessage = async (message) => {
    if (!replyMessage.trim()) return;
    
    setSendingReply(true);
    try {
      await apiClient.post('/contact/reply', {
        original_message_id: message.id,
        recipient_email: message.sender_email,
        recipient_name: message.sender_name,
        subject: `Re: ${message.subject}`,
        message: replyMessage,
        campaign_id: message.campaign_id
      });
      
      // Mark original as replied
      setContactMessages(prev => prev.map(m => 
        m.id === message.id ? { ...m, replied: true, replied_at: new Date().toISOString() } : m
      ));
      
      setReplyingTo(null);
      setReplyMessage('');
      alert('Reply sent successfully!');
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  // Mark message as read
  const handleMarkMessageRead = async (messageId) => {
    try {
      await apiClient.post(`/contact/mark-read/${messageId}`);
      setContactMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign? You can restore it later from the Deleted tab.')) {
      return;
    }
    setActionLoading(prev => ({ ...prev, [campaignId]: 'delete' }));
    try {
      await apiClient.delete(`/campaigns/${campaignId}`);
      // Move campaign to deleted list
      const campaign = myCampaigns.find(c => c.id === campaignId) || activeCampaigns.find(c => c.id === campaignId) || inactiveCampaigns.find(c => c.id === campaignId) || draftCampaigns.find(c => c.id === campaignId);
      if (campaign) {
        campaign.status = 'deleted';
        setDeletedCampaigns(prev => [...prev, campaign]);
      }
      setMyCampaigns(prev => prev.filter(c => c.id !== campaignId));
      setActiveCampaigns(prev => prev.filter(c => c.id !== campaignId));
      setInactiveCampaigns(prev => prev.filter(c => c.id !== campaignId));
      setDraftCampaigns(prev => prev.filter(c => c.id !== campaignId));
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      const errorDetail = err.response?.data?.detail || '';
      if (errorDetail.includes('donation') || errorDetail.includes('Cannot delete')) {
        alert('You cannot delete this campaign because donations have already been received.');
      } else {
        alert(errorDetail || 'Failed to delete campaign. Please try again.');
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: null }));
    }
  };

  const handleRestoreCampaign = async (campaignId) => {
    setActionLoading(prev => ({ ...prev, [campaignId]: 'restore' }));
    try {
      await apiClient.post(`/campaigns/${campaignId}/restore`);
      // Move campaign back to main list
      setDeletedCampaigns(prev => prev.filter(c => c.id !== campaignId));
      // Reload page to refresh campaigns
      window.location.reload();
    } catch (err) {
      console.error('Failed to restore campaign:', err);
      alert('Failed to restore campaign. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [campaignId]: null }));
    }
  };

  // Fetch user's subscriptions
  const fetchMySubscriptions = async () => {
    setSubscriptionsLoading(true);
    try {
      const response = await apiClient.get('/subscriptions/my-subscriptions');
      setMySubscriptions(response.data.subscriptions || []);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setMySubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  // Fetch collaboration invitations
  const fetchCollaborationInvites = async () => {
    setInvitesLoading(true);
    try {
      const response = await apiClient.get('/campaigns/my-invitations');
      setCollaborationInvites(response.data.invitations || []);
    } catch (err) {
      console.error('Failed to fetch collaboration invitations:', err);
      setCollaborationInvites([]);
    } finally {
      setInvitesLoading(false);
    }
  };

  // Respond to collaboration invitation
  const handleRespondToInvite = async (campaignId, action) => {
    setRespondingToInvite(campaignId);
    try {
      await apiClient.post(`/campaigns/${campaignId}/collaborators/respond`, { action });
      // Refresh invitations
      await fetchCollaborationInvites();
      alert(action === 'accept' ? 'You are now a collaborator on this campaign!' : 'Invitation declined');
    } catch (err) {
      console.error('Failed to respond to invitation:', err);
      alert(err.response?.data?.detail || 'Failed to respond to invitation. Please try again.');
    } finally {
      setRespondingToInvite(null);
    }
  };

  // Cancel a subscription
  const handleCancelSubscription = async (subscriptionId, stripeSubscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? This action cannot be undone.')) {
      return;
    }
    
    setCancellingSubscription(subscriptionId);
    try {
      await apiClient.post('/subscriptions/cancel', {
        subscription_id: subscriptionId,
        stripe_subscription_id: stripeSubscriptionId
      });
      // Refresh subscriptions
      await fetchMySubscriptions();
      alert('Subscription cancelled successfully');
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      alert(err.response?.data?.detail || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancellingSubscription(null);
    }
  };

  const mapCampaign = (c) => ({
    id: c.id,
    title: c.title,
    image: c.image,
    raised: c.raised,
    goal: c.goal,
    donors: c.donors,
    daysLeft: c.days_left,
    updates: c.updates || 0,
    status: c.status,
    type: c.type || 'campaign',
    category: c.category,
    milestones: c.milestones || [],
    enable_milestones: c.enable_milestones || false,
    team_fundraising_enabled: c.team_fundraising_enabled || false,
    created_at: c.created_at, // Added for date filtering and display
    currency: c.currency || 'USD',
    collaborators: c.collaborators || [],
    is_owner: c.is_owner ?? true, // Default to true if not set (owner's view)
    is_collaborator: c.is_collaborator ?? false, // Added for collaboration feature
  });

  // Scroll to top when dashboard loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      
      // Fetch verification status
      try {
        const verifyRes = await apiClient.get('/verification/kyc-status');
        setVerificationStatus(verifyRes.data);
      } catch (e) {
        console.error('Failed to fetch verification status:', e);
      }
      
      try {
        // Fetch all user's campaigns for overview (excluding drafts and deleted)
        const campaigns = await campaignAPI.getMyCampaigns({ exclude_deleted: true, exclude_drafts: true, limit: 100 });
        console.log('[Dashboard] getMyCampaigns returned:', campaigns?.length || 0, 'campaigns');
        
        if (campaigns && campaigns.length > 0) {
          setMyCampaigns(campaigns.map(mapCampaign));
        } else if (user?.campaigns_created > 0) {
          // User has campaigns according to profile, but API returned none - try dedicated endpoint
          console.warn('[Dashboard] User has campaigns but API returned none. Trying /auth/me/campaigns...');
          
          try {
            // Use the dedicated dashboard endpoint that uses the same query as /me stats
            const dashboardRes = await apiClient.get('/auth/me/campaigns');
            const dashboardCampaigns = dashboardRes.data || [];
            console.log('[Dashboard] /auth/me/campaigns returned:', dashboardCampaigns.length);
            if (dashboardCampaigns.length > 0) {
              const filteredCampaigns = dashboardCampaigns.filter(c => 
                c.status !== 'draft' && c.status !== 'deleted'
              );
              setMyCampaigns(filteredCampaigns.map(mapCampaign));
            } else {
              setMyCampaigns([]);
            }
          } catch (dashboardErr) {
            console.error('[Dashboard] /auth/me/campaigns failed:', dashboardErr);
            setMyCampaigns([]);
          }
        } else {
          setMyCampaigns([]);
        }
      } catch (e) {
        console.error('[Dashboard] Failed to fetch campaigns:', e);
        // Try the dedicated endpoint on error
        try {
          const dashboardRes = await apiClient.get('/auth/me/campaigns');
          const dashboardCampaigns = dashboardRes.data || [];
          const filteredCampaigns = dashboardCampaigns.filter(c => 
            c.status !== 'draft' && c.status !== 'deleted'
          );
          setMyCampaigns(filteredCampaigns.map(mapCampaign));
        } catch (dashboardErr) {
          console.error('[Dashboard] Fallback also failed:', dashboardErr);
          setMyCampaigns([]);
        }
      }

      try {
        // Fetch user's donations
        const donations = await donationAPI.getMyDonations();
        setMyDonations(donations.map(d => ({
          id: d.id,
          campaign: d.campaign_title,
          amount: d.amount,
          date: new Date(d.created_at).toLocaleDateString(),
          status: d.status,
        })));
      } catch (e) {
        console.error('Failed to fetch donations:', e);
        setMyDonations([]);
      }
      
      // Fetch collaboration invitations for badge count
      try {
        const invitesRes = await apiClient.get('/campaigns/my-invitations');
        setCollaborationInvites(invitesRes.data.invitations || []);
      } catch (e) {
        console.error('Failed to fetch collaboration invitations:', e);
      }
      
      setLoading(false);
    };

    fetchData();
  }, [user]);

  // Sync mode and tab with URL parameter
  useEffect(() => {
    const modeFromUrl = searchParams.get('mode');
    const tabFromUrl = searchParams.get('tab');
    if (modeFromUrl && modeFromUrl !== dashboardMode) {
      setDashboardMode(modeFromUrl);
    }
    if (tabFromUrl && tabFromUrl !== activeTab) {
      // Block ?tab=referrals when feature is disabled
      if (!REFERRALS_ENABLED && tabFromUrl === 'referrals') {
        setActiveTab('all');
      } else {
        setActiveTab(tabFromUrl);
      }
    }
  }, [searchParams]);

  // Update URL when mode changes
  const handleModeChange = (newMode) => {
    setDashboardMode(newMode);
    // Set default tab for each mode
    const defaultTab = newMode === 'creator' ? 'all' : 'backed';
    setActiveTab(defaultTab);
    setSearchParams({ mode: newMode, tab: defaultTab });
  };

  // Update URL when tab changes
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ mode: dashboardMode, tab: newTab });
  };

  // Fetch tab-specific data when tab changes
  useEffect(() => {
    if (user && activeTab !== 'all' && activeTab !== 'donations' && activeTab !== 'milestones') {
      fetchTabData(activeTab);
    }
    // Fetch subscriptions when subscriptions tab is active
    if (user && activeTab === 'subscriptions') {
      fetchMySubscriptions();
    }
    // Fetch collaboration invitations when invitations tab is active
    if (user && activeTab === 'invitations') {
      fetchCollaborationInvites();
    }
  }, [activeTab, user]);
  
  const isFullyVerified = verificationStatus.email_verified && 
    verificationStatus.phone_verified && 
    verificationStatus.kyc_status === 'approved';
  
  const hasPendingCampaigns = myCampaigns.some(c => c.status === 'pending_verification');

  // Export user's donations to CSV
  const handleExportDonations = () => {
    if (myDonations.length === 0) {
      alert('No donations to export');
      return;
    }
    
    // Create CSV content
    const headers = ['Date', 'Campaign', 'Amount', 'Currency', 'Status'];
    const rows = myDonations.map(d => [
      d.date || new Date(d.created_at).toLocaleDateString(),
      d.campaign || d.campaign_title || 'N/A',
      d.amount,
      d.currency || 'USD',
      d.status || 'completed'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my_donations_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Get data for current tab
  const getTabCampaigns = () => {
    switch (activeTab) {
      case 'active':
        return activeCampaigns;
      case 'drafts':
        return draftCampaigns;
      case 'projects':
        return projectCampaigns;
      default:
        return myCampaigns;
    }
  };

  const formatCurrencyValue = (value) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toLocaleString()}`;
  };

  // Mode-specific stats - calculate directly from myCampaigns with user profile fallbacks
  const activeCount = myCampaigns.length > 0 
    ? myCampaigns.filter(c => c.status === 'active').length 
    : (user?.active_campaigns || 0);
  const totalDonors = myCampaigns.length > 0 
    ? myCampaigns.reduce((sum, c) => sum + (c.donors || c.donor_count || 0), 0)
    : (user?.total_donors || 0);
  const campaignsCreated = myCampaigns.length > 0 ? myCampaigns.length : (user?.campaigns_created || 0);
  
  const creatorStats = [
    { label: t('totalRaised'), value: formatCurrencyValue(user?.total_raised || myCampaigns.reduce((sum, c) => sum + (c.raised || 0), 0)), change: '+12%', trend: 'up', icon: DollarSign, color: 'emerald' },
    { label: t('campaignsCreated') || 'Campaigns Created', value: String(campaignsCreated), change: '0', trend: 'neutral', icon: FileText, color: 'blue' },
    { label: t('activeCampaigns') || 'Active Campaigns', value: String(activeCount), change: '0', trend: 'neutral', icon: Rocket, color: 'teal' },
    { label: t('totalDonors') || 'Total Donors', value: String(totalDonors), change: '+5', trend: 'up', icon: Users, color: 'purple' },
  ];

  const donorStats = [
    { label: t('totalDonated'), value: formatCurrencyValue(user?.total_donated || backedProjects.total_amount_donated || 0), change: '+8%', trend: 'up', icon: Heart, color: 'rose' },
    { label: t('projectsBacked') || 'Projects Backed', value: String(backedProjects.total_projects_backed || 0), change: '+2', trend: 'up', icon: Target, color: 'teal' },
    { label: t('donationsMade') || 'Donations Made', value: String(user?.campaigns_donated_to || myDonations.length), change: '+5', trend: 'up', icon: Users, color: 'purple' },
    { label: t('impactScore') || 'Impact Score', value: String(Math.round((backedProjects.total_amount_donated || 0) / 100)), change: '+15', trend: 'up', icon: Star, color: 'emerald' },
  ];

  const stats = dashboardMode === 'creator' ? creatorStats : donorStats;

  const colorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    rose: 'bg-rose-500/20 text-rose-400',
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-teal-500/20 text-teal-400',
    teal: 'bg-teal-500/20 text-teal-400',
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };
  
  // Get currency symbol for a currency code
  const getCurrencySymbol = (currency = 'USD') => {
    const symbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$',
      'CHF': 'CHF', 'CNY': '¥', 'INR': '₹', 'MXN': 'MX$', 'BRL': 'R$', 'KRW': '₩',
      'SGD': 'S$', 'HKD': 'HK$', 'NZD': 'NZ$', 'SEK': 'kr', 'NOK': 'kr', 'DKK': 'kr',
      'ZAR': 'R', 'RUB': '₽', 'TRY': '₺', 'PLN': 'zł', 'THB': '฿', 'IDR': 'Rp',
      'MYR': 'RM', 'PHP': '₱', 'CZK': 'Kč', 'ILS': '₪', 'AED': 'د.إ', 'SAR': '﷼',
    };
    return symbols[currency] || '$';
  };

  // Check if email is not verified - show prominent banner (NOT for admin users)
  const emailNotVerified = !verificationStatus.email_verified && !['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user?.role);
  
  // Check if user is admin - redirect them to admin dashboard
  const isAdminUser = ['admin', 'platform_admin', 'super_admin', 'reviewer'].includes(user?.role);

  // Redirect admin users to admin dashboard
  useEffect(() => {
    if (isAdminUser) {
      navigate('/admin');
    }
  }, [isAdminUser, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Email Not Verified - VERY PROMINENT BANNER */}
        {emailNotVerified && (
          <div className="bg-gradient-to-r from-red-500 to-orange-500">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{t('verifyEmailToContinue') || 'Verify Your Email to Continue'}</h3>
                    <p className="text-white/90">
                      {t('checkInboxVerification') || 'Please check your inbox and click the verification link to access all features.'}
                    </p>
                  </div>
                </div>
                <Link to="/verify-email">
                  <Button size="lg" className="bg-white text-orange-600 hover:bg-white/90 font-bold shadow-lg">
                    <Mail className="w-5 h-5 mr-2" /> {t('verifyEmailNow') || 'Verify Email Now'}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Verification Banner - Show if email verified but not fully verified */}
        {!emailNotVerified && !isFullyVerified && (
          <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-b border-emerald-500/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{t('completeVerification') || 'Complete Your Verification'}</h3>
                    <p className="text-sm text-muted-foreground">
                      {hasPendingCampaigns 
                        ? t('campaignPendingVerification') || 'Your campaign is pending. Complete verification to go live!'
                        : t('getVerifiedMessage') || 'Get verified to receive donations and build trust with donors.'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* Email - Verified */}
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium">
                    <Mail className="w-4 h-4" />
                    <span>{t('email') || 'Email'}</span>
                    <BadgeCheck className="w-4 h-4" />
                  </div>
                  {/* Phone */}
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    verificationStatus.phone_verified 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-secondary/80 text-muted-foreground border border-dashed border-muted-foreground/30'
                  }`}>
                    <Phone className="w-4 h-4" />
                    <span>{t('phone') || 'Phone'}</span>
                    {verificationStatus.phone_verified && <BadgeCheck className="w-4 h-4" />}
                  </div>
                  {/* Documents */}
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    verificationStatus.kyc_status === 'approved' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : verificationStatus.kyc_status === 'pending'
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-secondary/80 text-muted-foreground border border-dashed border-muted-foreground/30'
                  }`}>
                    <FileCheck className="w-4 h-4" />
                    <span>ID</span>
                    {verificationStatus.kyc_status === 'approved' && <BadgeCheck className="w-4 h-4" />}
                    {verificationStatus.kyc_status === 'pending' && <Clock className="w-4 h-4" />}
                  </div>
                  <Link to="/verification">
                    <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-600 font-bold shadow-lg hover:shadow-xl transition-all">
                      <Shield className="w-5 h-5 mr-2" /> {t('completeVerification') || 'Complete Verification'}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Dashboard Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop'}
                    alt={user?.name || 'User'}
                    className="w-16 h-16 rounded-full border-4 border-white shadow-lg"
                  />
                  {user?.verified && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
                      <BadgeCheck className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <h1 className="text-lg sm:text-2xl font-bold text-foreground">{t('welcomeUser') || 'Welcome'}, {user?.name?.split(' ')[0] || 'User'}!</h1>
                    {user?.member_id && (
                      <Badge className="bg-emerald-500/20 text-emerald-500 font-mono text-xs sm:text-sm">
                        ID: {user.member_id}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center">
                    {user?.verified && <BadgeCheck className="w-4 h-4 text-primary mr-1" />}
                    {user?.verified ? t('verifiedAccount') || 'Verified' : t('account') || 'Account'}
                    <span className="hidden sm:inline"> • {t('memberSince') || 'Member since'} 2023</span>
                  </p>
                  {user?.member_id && (
                    <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
                      {t('shareIdForSupport') || 'Share your ID'} <span className="font-mono font-bold text-emerald-500">{user.member_id}</span> {t('forQuickSupport') || 'for quick support'}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4 md:mt-0">
                {/* Notifications Bell */}
                <div className="relative notification-dropdown">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="relative w-10 h-10"
                    onClick={() => setShowNotifications(!showNotifications)}
                  >
                    <Bell className="w-5 h-5" />
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-foreground text-xs rounded-full flex items-center justify-center">
                        {notifications.filter(n => !n.read).length}
                      </span>
                    )}
                  </Button>
                  
                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="p-3 border-b border-border flex items-center justify-between bg-secondary/50">
                        <h3 className="font-semibold text-foreground">Notifications</h3>
                        {notifications.filter(n => !n.read).length > 0 && (
                          <button 
                            onClick={markAllNotificationsRead}
                            className="text-xs text-emerald-500 hover:text-emerald-400"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-6 text-center text-muted-foreground">
                            <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No notifications yet</p>
                          </div>
                        ) : (
                          notifications.slice(0, 5).map((notif) => (
                            <div 
                              key={notif.id}
                              onClick={() => markNotificationRead(notif.id)}
                              className={`p-3 border-b border-border hover:bg-secondary/50 cursor-pointer transition-colors ${!notif.read ? 'bg-emerald-500/5' : ''}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!notif.read ? 'bg-emerald-500' : 'bg-transparent'}`} />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${!notif.read ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                                    {notif.message || notif.title}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {notif.time || notif.created_at || 'Just now'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="p-2 border-t border-border bg-secondary/30">
                        <Link to="/settings?tab=notifications" onClick={() => setShowNotifications(false)}>
                          <Button variant="ghost" size="sm" className="w-full text-emerald-500 hover:text-emerald-400">
                            View All Notifications
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                
                <Link to="/messages">
                  <Button variant="outline" size="sm" className="h-10 px-3">
                    <MessageCircle className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Messages</span>
                  </Button>
                </Link>
                <Link to="/wallet">
                  <Button variant="outline" size="sm" className="h-10 px-3 border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10">
                    <Wallet className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Wallet</span>
                  </Button>
                </Link>
                <Link to="/settings">
                  <Button variant="outline" size="sm" className="h-10 px-3">
                    <Settings className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Settings</span>
                  </Button>
                </Link>
                <Link to="/share-story" className="hidden md:block">
                  <Button variant="outline" size="sm" className="h-10 border-amber-500/50 text-amber-500 hover:bg-amber-500/10">
                    <Star className="w-4 h-4 mr-2" /> Share Story
                  </Button>
                </Link>
                <Link to="/create">
                  <Button size="sm" className="h-10 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                    <PlusCircle className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">New Campaign</span>
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Dashboard Mode Switcher */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-1 sm:gap-2 p-1 bg-secondary rounded-xl border border-border w-fit mx-auto">
              <Button
                variant={dashboardMode === 'creator' ? 'default' : 'ghost'}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all text-xs sm:text-sm ${
                  dashboardMode === 'creator' 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => handleModeChange('creator')}
              >
                <Rocket className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Creator Dashboard</span>
                <span className="sm:hidden">Creator</span>
              </Button>
              <Button
                variant={dashboardMode === 'donor' ? 'default' : 'ghost'}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all text-xs sm:text-sm ${
                  dashboardMode === 'donor' 
                    ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => handleModeChange('donor')}
              >
                <Heart className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Donor Dashboard</span>
                <span className="sm:hidden">Donor</span>
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">{stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index}>
                  <CardContent className="p-3 sm:p-6">
                    <div className="flex items-start justify-between">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${colorClasses[stat.color]}`}>
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      {stat.trend !== 'neutral' && (
                        <Badge variant={stat.trend === 'up' ? 'default' : 'destructive'} className="text-xs hidden sm:flex">
                          {stat.trend === 'up' ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                          {stat.change}
                        </Badge>
                      )}
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-foreground mt-2 sm:mt-4">{stat.value}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">{stat.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Main Content */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <TabsList className="bg-card border border-border p-1 rounded-lg inline-flex min-w-max md:flex-wrap">
              {dashboardMode === 'creator' ? (
                <>
                  {/* Creator Tabs */}
                  <TabsTrigger value="all" className="text-xs sm:text-sm whitespace-nowrap">{t('allCampaigns')}</TabsTrigger>
                  <TabsTrigger value="active" className="text-xs sm:text-sm whitespace-nowrap">{t('active')}</TabsTrigger>
                  <TabsTrigger value="received" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <DollarSign className="w-3 h-3 hidden sm:inline" /> <span className="sm:hidden">$</span> Donations
                  </TabsTrigger>
                  <TabsTrigger value="messages" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <MessageCircle className="w-3 h-3 hidden sm:inline" /> Messages
                    {contactMessages.filter(m => !m.read).length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                        {contactMessages.filter(m => !m.read).length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="inactive" className="text-xs sm:text-sm whitespace-nowrap">Inactive</TabsTrigger>
                  <TabsTrigger value="deleted" className="text-red-400 text-xs sm:text-sm whitespace-nowrap">Deleted</TabsTrigger>
                  <TabsTrigger value="drafts" className="text-xs sm:text-sm whitespace-nowrap">{t('savedDrafts')}</TabsTrigger>
                  <TabsTrigger value="milestones" className="text-xs sm:text-sm whitespace-nowrap">{t('milestones')}</TabsTrigger>
                  <TabsTrigger value="team-pages" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <Users className="w-3 h-3 hidden sm:inline" /> Team
                  </TabsTrigger>
                  {REFERRALS_ENABLED && (
                    <TabsTrigger value="referrals" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                      <Gift className="w-3 h-3 hidden sm:inline" /> Referrals
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="payouts" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <CreditCard className="w-3 h-3 hidden sm:inline" /> Payouts
                  </TabsTrigger>
                </>
              ) : (
                <>
                  {/* Donor Tabs */}
                  <TabsTrigger value="backed" className="text-xs sm:text-sm whitespace-nowrap">{t('backedProjects')}</TabsTrigger>
                  <TabsTrigger value="donations" className="text-xs sm:text-sm whitespace-nowrap">{t('donationHistory')}</TabsTrigger>
                  <TabsTrigger value="subscriptions" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <RefreshCw className="w-3 h-3 hidden sm:inline" /> Subscriptions
                  </TabsTrigger>
                  <TabsTrigger value="my-team-pages" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <Users className="w-3 h-3 hidden sm:inline" /> Team Pages
                  </TabsTrigger>
                  {REFERRALS_ENABLED && (
                    <TabsTrigger value="referrals" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                      <Gift className="w-3 h-3 hidden sm:inline" /> Referrals
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="invitations" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <Users className="w-3 h-3 hidden sm:inline" /> Invites
                    {collaborationInvites.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-emerald-500 text-white rounded-full">
                        {collaborationInvites.length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="my-messages" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <MessageCircle className="w-3 h-3 hidden sm:inline" /> Messages
                  </TabsTrigger>
                  <TabsTrigger value="disputes" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <ShieldAlert className="w-3 h-3 hidden sm:inline" /> Disputes
                  </TabsTrigger>
                  <TabsTrigger value="invoices" className="gap-1 text-xs sm:text-sm whitespace-nowrap">
                    <FileText className="w-3 h-3 hidden sm:inline" /> Invoices
                  </TabsTrigger>
                </>
              )}
              {/* Shared Tab */}
              <TabsTrigger value="notifications" className="text-xs sm:text-sm whitespace-nowrap">{t('notifications')}</TabsTrigger>
            </TabsList>
            </div>

            {/* All Campaigns Tab */}
            <TabsContent value="all" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <CardTitle>{t('allCampaigns')}</CardTitle>
                        <CardDescription>View all your campaigns and projects (active, completed, inactive)</CardDescription>
                      </div>
                      {/* Type Filter Buttons */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-muted-foreground mr-1"><Filter className="w-4 h-4 inline mr-1" />Filter:</span>
                        <Button 
                          size="sm" 
                          variant={typeFilter === 'all' ? 'default' : 'outline'}
                          className={typeFilter === 'all' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                          onClick={() => setTypeFilter('all')}
                        >
                          All
                        </Button>
                        <Button 
                          size="sm" 
                          variant={typeFilter === 'campaign' ? 'default' : 'outline'}
                          className={typeFilter === 'campaign' ? 'bg-pink-500 hover:bg-pink-600' : ''}
                          onClick={() => setTypeFilter('campaign')}
                        >
                          <Heart className="w-3 h-3 mr-1" /> Campaign
                        </Button>
                        <Button 
                          size="sm" 
                          variant={typeFilter === 'project' ? 'default' : 'outline'}
                          className={typeFilter === 'project' ? 'bg-teal-500 hover:bg-teal-600' : ''}
                          onClick={() => setTypeFilter('project')}
                        >
                          <Rocket className="w-3 h-3 mr-1" /> Creator Project
                        </Button>
                      </div>
                    </div>
                    {/* Search Input */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          data-testid="campaign-search-input"
                          type="text"
                          placeholder="Search by title, keyword, or campaign ID..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1); // Reset to first page on search
                          }}
                          className="pl-10 pr-10"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setCurrentPage(1);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {/* Date Filter - Filter by Created Date Range */}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <Select value={dateFilter} onValueChange={(value) => {
                          setDateFilter(value);
                          setCurrentPage(1);
                        }}>
                          <SelectTrigger data-testid="campaign-date-filter" className="w-[150px]">
                            <SelectValue placeholder="Date Range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Time</SelectItem>
                            <SelectItem value="today">Today</SelectItem>
                            <SelectItem value="week">This Week</SelectItem>
                            <SelectItem value="month">This Month</SelectItem>
                            <SelectItem value="3months">Last 3 Months</SelectItem>
                            <SelectItem value="year">This Year</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Sort Dropdown */}
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                        <Select value={sortBy} onValueChange={(value) => {
                          setSortBy(value);
                          setCurrentPage(1);
                        }}>
                          <SelectTrigger data-testid="campaign-sort-select" className="w-[160px]">
                            <SelectValue placeholder="Sort by..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="newest">Newest First</SelectItem>
                            <SelectItem value="oldest">Oldest First</SelectItem>
                            <SelectItem value="most_raised">Most Raised</SelectItem>
                            <SelectItem value="least_raised">Least Raised</SelectItem>
                            <SelectItem value="most_donors">Most Donors</SelectItem>
                            <SelectItem value="goal_high">Highest Goal</SelectItem>
                            <SelectItem value="goal_low">Lowest Goal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : (() => {
                    // Combine ALL campaigns: myCampaigns + activeCampaigns + inactiveCampaigns
                    // Remove duplicates and filter out drafts and deleted
                    const campaignMap = new Map();
                    
                    // Add all campaigns to map (using id as key to avoid duplicates)
                    [...myCampaigns, ...activeCampaigns, ...inactiveCampaigns].forEach(campaign => {
                      if (campaign.status !== 'draft' && campaign.status !== 'deleted') {
                        campaignMap.set(campaign.id, campaign);
                      }
                    });
                    
                    // Convert map back to array
                    const allItems = Array.from(campaignMap.values());
                    
                    // Apply type filter
                    let filteredItems = typeFilter === 'all' 
                      ? allItems 
                      : allItems.filter(c => c.type === typeFilter);
                    
                    // Apply search filter - now includes campaign ID search
                    if (searchQuery.trim()) {
                      const query = searchQuery.toLowerCase().trim();
                      filteredItems = filteredItems.filter(c => 
                        (c.id && c.id.toLowerCase().includes(query)) ||
                        (c.title && c.title.toLowerCase().includes(query)) ||
                        (c.story && c.story.toLowerCase().includes(query)) ||
                        (c.category && c.category.toLowerCase().includes(query)) ||
                        (c.beneficiary && c.beneficiary.toLowerCase().includes(query))
                      );
                    }
                    
                    // Apply date range filter
                    if (dateFilter !== 'all') {
                      const now = new Date();
                      let cutoffDate = new Date();
                      
                      switch (dateFilter) {
                        case 'today':
                          cutoffDate.setHours(0, 0, 0, 0);
                          break;
                        case 'week':
                          cutoffDate.setDate(now.getDate() - 7);
                          break;
                        case 'month':
                          cutoffDate.setMonth(now.getMonth() - 1);
                          break;
                        case '3months':
                          cutoffDate.setMonth(now.getMonth() - 3);
                          break;
                        case 'year':
                          cutoffDate.setFullYear(now.getFullYear() - 1);
                          break;
                        default:
                          break;
                      }
                      
                      filteredItems = filteredItems.filter(c => {
                        const createdAt = new Date(c.created_at || 0);
                        return createdAt >= cutoffDate;
                      });
                    }
                    
                    // Apply sorting
                    filteredItems.sort((a, b) => {
                      switch (sortBy) {
                        case 'newest':
                          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                        case 'oldest':
                          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                        case 'most_raised':
                          return (b.raised || 0) - (a.raised || 0);
                        case 'least_raised':
                          return (a.raised || 0) - (b.raised || 0);
                        case 'most_donors':
                          return (b.donors || 0) - (a.donors || 0);
                        case 'goal_high':
                          return (b.goal || 0) - (a.goal || 0);
                        case 'goal_low':
                          return (a.goal || 0) - (b.goal || 0);
                        default:
                          return 0;
                      }
                    });
                    
                    // Pagination calculations
                    const totalItems = filteredItems.length;
                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedItems = filteredItems.slice(startIndex, endIndex);
                    
                    return filteredItems.length > 0 ? (
                      <div className="space-y-4">
                        {/* Results info */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <p className="text-sm text-muted-foreground">
                            {searchQuery && `Found ${totalItems} result${totalItems !== 1 ? 's' : ''} for "${searchQuery}"`}
                            {searchQuery && sortBy !== 'newest' && ' • '}
                            {sortBy !== 'newest' && `Sorted by: ${sortBy.replace('_', ' ')}`}
                            {!searchQuery && sortBy === 'newest' && `Showing ${startIndex + 1}-${Math.min(endIndex, totalItems)} of ${totalItems} campaigns`}
                          </p>
                          {/* Items per page selector */}
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>Show:</span>
                            <Select value={itemsPerPage.toString()} onValueChange={(value) => {
                              setItemsPerPage(parseInt(value));
                              setCurrentPage(1);
                            }}>
                              <SelectTrigger className="w-[70px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="5">5</SelectItem>
                                <SelectItem value="10">10</SelectItem>
                                <SelectItem value="20">20</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                              </SelectContent>
                            </Select>
                            <span>per page</span>
                          </div>
                        </div>
                        
                        {/* Campaign list */}
                        {paginatedItems.map((campaign) => (
                          <CampaignListItem 
                            key={campaign.id} 
                            campaign={campaign} 
                            formatCurrency={formatCurrency}
                            showTypeLabel={true}
                            showCampaignId={true}
                            showActions={true}
                            onPause={() => handlePauseCampaign(campaign.id)}
                            onResume={() => handleResumeCampaign(campaign.id)}
                            onDelete={() => handleDeleteCampaign(campaign.id)}
                            actionLoading={actionLoading[campaign.id]}
                          />
                        ))}
                        
                        {/* Pagination controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 pt-4 border-t border-border">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                              data-testid="pagination-first"
                            >
                              First
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              data-testid="pagination-prev"
                            >
                              Previous
                            </Button>
                            <span className="px-4 py-2 text-sm text-muted-foreground">
                              Page {currentPage} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              data-testid="pagination-next"
                            >
                              Next
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(totalPages)}
                              disabled={currentPage === totalPages}
                              data-testid="pagination-last"
                            >
                              Last
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">
                          {searchQuery 
                            ? `No campaigns found matching "${searchQuery}"`
                            : typeFilter === 'all' 
                              ? 'No campaigns or projects yet' 
                              : `No ${typeFilter === 'project' ? 'creator projects' : 'campaigns'} yet`
                          }
                        </p>
                        {searchQuery && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-4"
                            onClick={() => setSearchQuery('')}
                          >
                            Clear search
                          </Button>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Active Campaigns Tab */}
            <TabsContent value="active" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <CardTitle>Active Campaigns & Projects</CardTitle>
                      <CardDescription>Currently running campaigns and projects accepting donations</CardDescription>
                    </div>
                    {/* Type Filter + Sort Controls */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-muted-foreground mr-1"><Filter className="w-4 h-4 inline mr-1" />Filter:</span>
                      <Button 
                        size="sm" 
                        variant={typeFilter === 'all' ? 'default' : 'outline'}
                        className={typeFilter === 'all' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                        onClick={() => setTypeFilter('all')}
                      >
                        All
                      </Button>
                      <Button 
                        size="sm" 
                        variant={typeFilter === 'campaign' ? 'default' : 'outline'}
                        className={typeFilter === 'campaign' ? 'bg-pink-500 hover:bg-pink-600' : ''}
                        onClick={() => setTypeFilter('campaign')}
                      >
                        <Heart className="w-3 h-3 mr-1" /> Campaign
                      </Button>
                      <Button 
                        size="sm" 
                        variant={typeFilter === 'project' ? 'default' : 'outline'}
                        className={typeFilter === 'project' ? 'bg-teal-500 hover:bg-teal-600' : ''}
                        onClick={() => setTypeFilter('project')}
                      >
                        <Rocket className="w-3 h-3 mr-1" /> Creator Project
                      </Button>
                      <div className="h-4 w-px bg-border mx-1" />
                      {/* Date Filter */}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <Select value={dateFilter} onValueChange={(value) => { setDateFilter(value); setCurrentPage(1); }}>
                          <SelectTrigger data-testid="active-date-filter" className="w-[120px] h-8 text-xs">
                            <SelectValue placeholder="Date" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Time</SelectItem>
                            <SelectItem value="today">Today</SelectItem>
                            <SelectItem value="week">This Week</SelectItem>
                            <SelectItem value="month">This Month</SelectItem>
                            <SelectItem value="3months">Last 3 Months</SelectItem>
                            <SelectItem value="year">This Year</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Sort by Date */}
                      <Select value={sortBy} onValueChange={(value) => { setSortBy(value); setCurrentPage(1); }}>
                        <SelectTrigger data-testid="active-sort-select" className="w-[140px] h-8 text-xs">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Newest First</SelectItem>
                          <SelectItem value="oldest">Oldest First</SelectItem>
                          <SelectItem value="most_raised">Most Raised</SelectItem>
                          <SelectItem value="most_donors">Most Donors</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Bulk Actions Bar for Active */}
                  {selectedActive.length > 0 && (
                    <div className="flex items-center gap-3 mt-4 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                      <span className="text-sm text-emerald-400 font-medium">{selectedActive.length} selected</span>
                      <Button size="sm" variant="outline" className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20" 
                        disabled={bulkActionLoading}
                        onClick={() => handleBulkAction('pause', selectedActive, setSelectedActive)}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Pause className="w-4 h-4 mr-1" />} Pause All
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        disabled={bulkActionLoading}
                        onClick={() => handleBulkAction('delete', selectedActive, setSelectedActive)}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete All
                      </Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setSelectedActive([])}>
                        Clear
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loading || tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : (() => {
                    // Filter active campaigns by type
                    let filteredActive = typeFilter === 'all' 
                      ? activeCampaigns 
                      : activeCampaigns.filter(c => c.type === typeFilter);
                    
                    // Apply date range filter
                    if (dateFilter !== 'all') {
                      const now = new Date();
                      let cutoffDate = new Date();
                      
                      switch (dateFilter) {
                        case 'today':
                          cutoffDate.setHours(0, 0, 0, 0);
                          break;
                        case 'week':
                          cutoffDate.setDate(now.getDate() - 7);
                          break;
                        case 'month':
                          cutoffDate.setMonth(now.getMonth() - 1);
                          break;
                        case '3months':
                          cutoffDate.setMonth(now.getMonth() - 3);
                          break;
                        case 'year':
                          cutoffDate.setFullYear(now.getFullYear() - 1);
                          break;
                        default:
                          break;
                      }
                      
                      filteredActive = filteredActive.filter(c => {
                        const createdAt = new Date(c.created_at || 0);
                        return createdAt >= cutoffDate;
                      });
                    }
                    
                    // Sort campaigns
                    filteredActive = [...filteredActive].sort((a, b) => {
                      switch (sortBy) {
                        case 'oldest':
                          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                        case 'most_raised':
                          return (b.current_amount || b.raised || 0) - (a.current_amount || a.raised || 0);
                        case 'most_donors':
                          return (b.donors || 0) - (a.donors || 0);
                        case 'newest':
                        default:
                          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                      }
                    });
                    
                    // Pagination
                    const totalItems = filteredActive.length;
                    const totalPages = Math.ceil(totalItems / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const paginatedCampaigns = filteredActive.slice(startIndex, startIndex + itemsPerPage);
                    
                    return totalItems > 0 ? (
                      <div className="space-y-4">
                        {/* Stats bar */}
                        <div className="flex items-center justify-between text-sm text-muted-foreground pb-3 border-b border-border">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selectedActive.length === paginatedCampaigns.length && paginatedCampaigns.length > 0}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedActive(paginatedCampaigns.map(c => c.id));
                                else setSelectedActive([]);
                              }}
                            />
                            <span>Select All ({paginatedCampaigns.length})</span>
                          </div>
                          <span>Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems}</span>
                        </div>
                        
                        {paginatedCampaigns.map((campaign) => (
                          <div key={campaign.id} className="flex items-start gap-3">
                            <div className="pt-4" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedActive.includes(campaign.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) setSelectedActive(prev => [...prev, campaign.id]);
                                  else setSelectedActive(prev => prev.filter(id => id !== campaign.id));
                                }}
                              />
                            </div>
                            <div className="flex-1">
                              <CampaignListItem 
                                campaign={campaign} 
                                formatCurrency={formatCurrency}
                                showTypeLabel={true}
                                onPause={() => handlePauseCampaign(campaign.id)}
                                onDelete={() => handleDeleteCampaign(campaign.id)}
                                actionLoading={actionLoading[campaign.id]}
                              />
                            </div>
                          </div>
                        ))}
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 pt-4 border-t border-border">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(1)}
                            >
                              First
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(prev => prev - 1)}
                            >
                              Previous
                            </Button>
                            <span className="px-4 text-sm text-muted-foreground">
                              Page {currentPage} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(prev => prev + 1)}
                            >
                              Next
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={currentPage === totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                            >
                              Last
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground mb-4">
                          {typeFilter === 'all' ? 'No active campaigns or projects' : `No active ${typeFilter}s`}
                        </p>
                        <Link to={`/create?type=${typeFilter === 'project' ? 'project' : 'campaign'}`}>
                          <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
                            <PlusCircle className="w-4 h-4 mr-2" /> Start a {typeFilter === 'project' ? 'Project' : 'Campaign'}
                          </Button>
                        </Link>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Inactive (Paused) Campaigns Tab */}
            <TabsContent value="inactive" className="space-y-6">
              <Card>
                <CardHeader>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Pause className="w-5 h-5 text-yellow-500" /> Inactive Campaigns
                    </CardTitle>
                    <CardDescription>Paused campaigns that are not accepting donations</CardDescription>
                  </div>
                  {/* Bulk Actions Bar for Inactive */}
                  {selectedInactive.length > 0 && (
                    <div className="flex items-center gap-3 mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                      <span className="text-sm text-yellow-400 font-medium">{selectedInactive.length} selected</span>
                      <Button size="sm" variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/20"
                        disabled={bulkActionLoading}
                        onClick={() => handleBulkAction('resume', selectedInactive, setSelectedInactive)}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />} Resume All
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        disabled={bulkActionLoading}
                        onClick={() => handleBulkAction('delete', selectedInactive, setSelectedInactive)}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete All
                      </Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setSelectedInactive([])}>
                        Clear
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loading || tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : inactiveCampaigns.length > 0 ? (
                    <div className="space-y-4">
                      {/* Select All */}
                      <div className="flex items-center gap-3 pb-3 border-b border-border">
                        <Checkbox
                          checked={selectedInactive.length === inactiveCampaigns.length && inactiveCampaigns.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedInactive(inactiveCampaigns.map(c => c.id));
                            else setSelectedInactive([]);
                          }}
                        />
                        <span className="text-sm text-muted-foreground">Select All ({inactiveCampaigns.length})</span>
                      </div>
                      {inactiveCampaigns.map((campaign) => (
                        <div key={campaign.id} className="flex items-start gap-3">
                          <div className="pt-4" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedInactive.includes(campaign.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedInactive(prev => [...prev, campaign.id]);
                                else setSelectedInactive(prev => prev.filter(id => id !== campaign.id));
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <CampaignListItem 
                              campaign={campaign} 
                              formatCurrency={formatCurrency}
                              showTypeLabel={true}
                              showActions={true}
                              onResume={() => handleResumeCampaign(campaign.id)}
                              onDelete={() => handleDeleteCampaign(campaign.id)}
                              actionLoading={actionLoading[campaign.id]}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Pause className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No paused campaigns</p>
                      <p className="text-sm text-muted-foreground mt-2">Paused campaigns will appear here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Deleted Campaigns Tab */}
            <TabsContent value="deleted" className="space-y-6">
              <Card className="border-red-500/20">
                <CardHeader>
                  <div>
                    <CardTitle className="flex items-center gap-2 text-red-400">
                      <Trash2 className="w-5 h-5" /> Deleted Campaigns
                    </CardTitle>
                    <CardDescription>Deleted campaigns can be restored within 30 days</CardDescription>
                  </div>
                  {/* Bulk Actions Bar for Deleted */}
                  {selectedDeleted.length > 0 && (
                    <div className="flex items-center gap-3 mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                      <span className="text-sm text-red-400 font-medium">{selectedDeleted.length} selected</span>
                      <Button size="sm" variant="outline" className="border-green-500/30 text-green-400 hover:bg-green-500/20"
                        disabled={bulkActionLoading}
                        onClick={() => handleBulkAction('restore', selectedDeleted, setSelectedDeleted)}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />} Restore All
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        disabled={bulkActionLoading}
                        onClick={() => handleBulkAction('permanent_delete', selectedDeleted, setSelectedDeleted)}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete Permanently
                      </Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setSelectedDeleted([])}>
                        Clear
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loading || tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : deletedCampaigns.length > 0 ? (
                    <div className="space-y-4">
                      {/* Select All */}
                      <div className="flex items-center gap-3 pb-3 border-b border-border">
                        <Checkbox
                          checked={selectedDeleted.length === deletedCampaigns.length && deletedCampaigns.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedDeleted(deletedCampaigns.map(c => c.id));
                            else setSelectedDeleted([]);
                          }}
                        />
                        <span className="text-sm text-muted-foreground">Select All ({deletedCampaigns.length})</span>
                      </div>
                      {deletedCampaigns.map((campaign) => (
                        <div key={campaign.id} className="flex items-start gap-3">
                          <div className="pt-4" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedDeleted.includes(campaign.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedDeleted(prev => [...prev, campaign.id]);
                                else setSelectedDeleted(prev => prev.filter(id => id !== campaign.id));
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <CampaignListItem 
                              campaign={campaign} 
                              formatCurrency={formatCurrency}
                              showTypeLabel={true}
                              showRestoreAction={true}
                              onRestore={() => handleRestoreCampaign(campaign.id)}
                              actionLoading={actionLoading[campaign.id]}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Trash2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No deleted campaigns</p>
                      <p className="text-sm text-muted-foreground mt-2">Deleted campaigns will appear here for 30 days</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Saved Drafts Tab */}
            <TabsContent value="drafts" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Saved Drafts</CardTitle>
                      <CardDescription>Continue editing your saved campaign drafts</CardDescription>
                    </div>
                  </div>
                  {/* Bulk Actions Bar for Drafts */}
                  {selectedDrafts.length > 0 && (
                    <div className="flex items-center gap-3 mt-4 p-3 bg-slate-500/10 rounded-lg border border-border/30">
                      <span className="text-sm text-muted-foreground font-medium">{selectedDrafts.length} selected</span>
                      <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                        disabled={bulkActionLoading}
                        onClick={() => handleBulkAction('permanent_delete', selectedDrafts, setSelectedDrafts)}>
                        {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete All
                      </Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setSelectedDrafts([])}>
                        Clear
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {loading || tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : draftCampaigns.length > 0 ? (
                    <div className="space-y-4">
                      {/* Select All */}
                      <div className="flex items-center gap-3 pb-3 border-b border-border">
                        <Checkbox
                          checked={selectedDrafts.length === draftCampaigns.length && draftCampaigns.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedDrafts(draftCampaigns.map(c => c.id));
                            else setSelectedDrafts([]);
                          }}
                        />
                        <span className="text-sm text-muted-foreground">Select All ({draftCampaigns.length})</span>
                      </div>
                      {draftCampaigns.map((campaign) => (
                        <div key={campaign.id} className="flex items-start gap-3">
                          <div className="pt-4" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedDrafts.includes(campaign.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedDrafts(prev => [...prev, campaign.id]);
                                else setSelectedDrafts(prev => prev.filter(id => id !== campaign.id));
                              }}
                            />
                          </div>
                          <div 
                            className="flex-1 p-4 bg-secondary rounded-lg border border-border border-dashed cursor-pointer hover:bg-secondary/80 hover:border-emerald-500/50 transition-all"
                            onClick={(e) => {
                              if (e.target.closest('button')) return;
                              window.location.href = `/create?draft=${campaign.id}`;
                            }}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start space-x-4 flex-1">
                                <img
                                  src={campaign.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.title)}&size=128&background=0f172a&color=22d3ee&bold=true`}
                                  alt={campaign.title}
                                  className="w-20 h-20 rounded-lg object-cover opacity-70"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <Badge className="bg-gray-500/20 text-gray-400">Draft</Badge>
                                    <Badge variant="outline" className="text-xs capitalize">{campaign.type || 'campaign'}</Badge>
                                    {campaign.category && (
                                      <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-500/30">{campaign.category}</Badge>
                                    )}
                                  </div>
                                  <h4 className="font-semibold text-foreground mb-1">{campaign.title || 'Untitled Draft'}</h4>
                                  {campaign.description && (
                                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{campaign.description}</p>
                                  )}
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    <span>Goal: <span className="text-foreground">{formatCurrency(campaign.goal || 0, campaign.currency)}</span></span>
                                    {campaign.location && (
                                      <span>Location: <span className="text-foreground">{campaign.location}</span></span>
                                    )}
                                    {campaign.created_at && (
                                      <span>Created: <span className="text-foreground">{new Date(campaign.created_at).toLocaleDateString()}</span></span>
                                    )}
                                  </div>
                                  {/* Show what's missing */}
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {!campaign.image && <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">Missing: Image</span>}
                                    {!campaign.description && <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">Missing: Description</span>}
                                    {(!campaign.goal || campaign.goal === 0) && <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">Missing: Goal</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Link to={`/create?draft=${campaign.id}`}>
                                  <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-600 w-full">
                                    <Edit className="w-4 h-4 mr-1" /> Continue
                                  </Button>
                                </Link>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-red-400 hover:bg-red-500/10"
                                  onClick={() => handleDeleteCampaign(campaign.id, campaign.title)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">No saved drafts</p>
                      <Link to="/create">
                        <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
                          <PlusCircle className="w-4 h-4 mr-2" /> Start a New Campaign
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Projects Tab - Uses API filtered data */}
            <TabsContent value="projects" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>My Projects</CardTitle>
                      <CardDescription>Long-term projects seeking funding</CardDescription>
                    </div>
                    <Link to="/create?type=project">
                      <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                        <PlusCircle className="w-4 h-4 mr-2" /> New Project
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading || tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : projectCampaigns.length > 0 ? (
                    <div className="space-y-4">
                      {projectCampaigns.map((project) => (
                        <CampaignListItem key={project.id} campaign={project} formatCurrency={formatCurrency} isProject />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">No projects yet</p>
                      <Link to="/create?type=project">
                        <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
                          <PlusCircle className="w-4 h-4 mr-2" /> Create Your First Project
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Milestones Tab */}
            <TabsContent value="milestones" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-400" />
                    Milestone Activity
                  </CardTitle>
                  <CardDescription>Track milestone progress, fund releases, and proof uploads for your campaigns</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : myCampaigns.filter(c => c.milestones && c.milestones.length > 0).length > 0 ? (
                    <div className="space-y-6">
                      {myCampaigns.filter(c => c.milestones && c.milestones.length > 0).map((campaign) => {
                        const completedMilestones = campaign.milestones.filter(m => m.status === 'completed').length;
                        const totalMilestones = campaign.milestones.length;
                        const progressPercent = totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;
                        const currentMilestone = campaign.milestones.find(m => m.status === 'current') || campaign.milestones.find(m => m.status === 'pending');
                        
                        return (
                          <div key={campaign.id} className="p-4 bg-secondary rounded-lg border border-border">
                            {/* Campaign Header */}
                            <div className="flex items-center gap-3 mb-4">
                              <img
                                src={campaign.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.title)}&size=64&background=0f172a&color=22d3ee`}
                                alt={campaign.title}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                              <div className="flex-1">
                                <h4 className="font-semibold text-foreground">{campaign.title}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {completedMilestones} of {totalMilestones} milestones completed
                                </p>
                              </div>
                              <Link to={`/campaign/${campaign.id}`}>
                                <Button variant="outline" size="sm">
                                  View Campaign
                                </Button>
                              </Link>
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="mb-4">
                              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                <span>Milestone Progress</span>
                                <span>{Math.round(progressPercent)}%</span>
                              </div>
                              <Progress value={progressPercent} className="h-2" />
                            </div>
                            
                            {/* Milestones List */}
                            <div className="space-y-2">
                              {campaign.milestones.map((milestone, idx) => (
                                <div 
                                  key={idx} 
                                  className={`flex items-center gap-3 p-3 rounded-lg ${
                                    milestone.status === 'completed' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                                    milestone.status === 'current' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                                    'bg-card/50 border border-border'
                                  }`}
                                >
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    milestone.status === 'completed' ? 'bg-emerald-500' :
                                    milestone.status === 'current' ? 'bg-emerald-500' :
                                    'bg-secondary'
                                  }`}>
                                    {milestone.status === 'completed' ? (
                                      <CheckCircle2 className="w-4 h-4 text-white" />
                                    ) : milestone.status === 'current' ? (
                                      <Flag className="w-4 h-4 text-white" />
                                    ) : (
                                      <Circle className="w-4 h-4 text-muted-foreground" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <p className={`font-medium ${
                                      milestone.status === 'completed' ? 'text-emerald-400' :
                                      milestone.status === 'current' ? 'text-emerald-400' :
                                      'text-muted-foreground'
                                    }`}>
                                      {milestone.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {milestone.description || `Target: ${formatCurrency(milestone.amount)}`}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-foreground">{formatCurrency(milestone.amount)}</p>
                                    <p className={`text-xs capitalize ${
                                      milestone.status === 'completed' ? 'text-emerald-400' :
                                      milestone.status === 'current' ? 'text-emerald-400' :
                                      'text-muted-foreground'
                                    }`}>
                                      {milestone.status === 'current' ? 'In Progress' : milestone.status}
                                    </p>
                                  </div>
                                  {milestone.status === 'current' && (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="border-emerald-500/50 text-emerald-400"
                                      onClick={() => {
                                        setSelectedMilestone({ ...milestone, campaign_id: campaign.id });
                                        setShowProofUploadDialog(true);
                                      }}
                                    >
                                      <Upload className="w-3 h-3 mr-1" /> Upload Proof
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                            
                            {/* Fund Release Info */}
                            {completedMilestones > 0 && (
                              <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                                <div className="flex items-center gap-2 text-emerald-400">
                                  <Wallet className="w-4 h-4" />
                                  <span className="text-sm font-medium">
                                    Funds Released: {formatCurrency(
                                      campaign.milestones
                                        .filter(m => m.status === 'completed')
                                        .reduce((sum, m) => sum + m.amount, 0)
                                    )}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Target className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Milestones Yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Create a campaign with milestones to track your progress and build trust with donors.
                      </p>
                      <Link to="/create">
                        <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
                          <PlusCircle className="w-4 h-4 mr-2" /> Create Campaign with Milestones
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="campaigns">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>My Campaigns</CardTitle>
                    <Link to="/create">
                      <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                        <PlusCircle className="w-4 h-4 mr-2" /> Create New
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : myCampaigns.length > 0 ? (
                    <div className="space-y-4">
                      {myCampaigns.map((campaign) => (
                        <CampaignListItem key={campaign.id} campaign={campaign} formatCurrency={formatCurrency} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">You haven't created any campaigns yet.</p>
                      <Link to="/create">
                        <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                          <PlusCircle className="w-4 h-4 mr-2" /> Create Your First Campaign
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Donations Received Tab - For Creators */}
            <TabsContent value="received">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                        Donations Received
                      </CardTitle>
                      <CardDescription>
                        View all donations received on your campaigns
                      </CardDescription>
                    </div>
                    {donationsReceived.total_amount > 0 && (
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-400">{formatCurrency(donationsReceived.total_amount)}</p>
                        <p className="text-xs text-muted-foreground">Total received</p>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : donationsReceived.donations?.length > 0 ? (
                    <div className="space-y-4">
                      {/* Stats Summary */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="p-4 bg-secondary rounded-lg text-center">
                          <p className="text-2xl font-bold text-foreground">{donationsReceived.total_received}</p>
                          <p className="text-xs text-muted-foreground">Total Donations</p>
                        </div>
                        <div className="p-4 bg-secondary rounded-lg text-center">
                          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(donationsReceived.total_amount)}</p>
                          <p className="text-xs text-muted-foreground">Total Amount</p>
                        </div>
                        <div className="p-4 bg-secondary rounded-lg text-center">
                          <p className="text-2xl font-bold text-teal-400">{donationsReceived.unique_donors}</p>
                          <p className="text-xs text-muted-foreground">Unique Donors</p>
                        </div>
                      </div>

                      {/* Donations List */}
                      <div className="space-y-3">
                        {donationsReceived.donations.map((donation) => (
                          <div 
                            key={donation.id} 
                            className="p-4 bg-secondary rounded-lg border border-border hover:border-emerald-500/50 transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                  <DollarSign className="w-5 h-5 text-emerald-400" />
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">
                                    {donation.anonymous ? 'Anonymous' : (donation.donor_name || 'Anonymous Donor')}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {donation.campaign_title || 'Unknown Campaign'}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-emerald-400">{formatCurrency(donation.amount)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(donation.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            {donation.message && (
                              <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                                <p className="text-sm text-muted-foreground italic">"{donation.message}"</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <DollarSign className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mb-4">No donations received yet.</p>
                      <p className="text-sm text-muted-foreground">Share your campaigns to start receiving donations!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Messages Tab - For Creators */}
            <TabsContent value="messages">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-blue-400" />
                        Messages from Supporters
                      </CardTitle>
                      <CardDescription>
                        View and reply to messages from people interested in your campaigns
                      </CardDescription>
                    </div>
                    {contactMessages.length > 0 && (
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-400">{contactMessages.length}</p>
                        <p className="text-xs text-muted-foreground">Total messages</p>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : contactMessages.length > 0 ? (
                    <div className="space-y-4">
                      {contactMessages.map((message) => (
                        <div 
                          key={message.id} 
                          className={`p-4 rounded-lg border transition-all ${
                            message.read 
                              ? 'bg-secondary border-border' 
                              : 'bg-blue-500/10 border-blue-500/30'
                          }`}
                          onClick={() => !message.read && handleMarkMessageRead(message.id)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                message.read ? 'bg-secondary' : 'bg-blue-500/20'
                              }`}>
                                <Mail className={`w-5 h-5 ${message.read ? 'text-muted-foreground' : 'text-blue-400'}`} />
                              </div>
                              <div>
                                <p className="font-medium text-foreground flex items-center gap-2">
                                  {message.sender_name}
                                  {!message.read && (
                                    <span className="px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">New</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {message.sender_email || 'No email provided'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">
                                {new Date(message.created_at).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Re: {message.campaign_title}
                              </p>
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <p className="text-sm font-medium text-foreground mb-1">{message.subject}</p>
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{message.message}</p>
                          </div>
                          
                          {message.replied ? (
                            <div className="flex items-center gap-2 text-xs text-emerald-400">
                              <CheckCircle2 className="w-4 h-4" />
                              Replied on {new Date(message.replied_at).toLocaleDateString()}
                            </div>
                          ) : replyingTo?.id === message.id ? (
                            <div className="mt-3 space-y-3 border-t border-border pt-3">
                              <Textarea
                                placeholder="Write your reply..."
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                className="bg-background min-h-[100px]"
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleReplyToMessage(message)}
                                  disabled={!replyMessage.trim() || sendingReply}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  {sendingReply ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                                  ) : (
                                    <><Send className="w-4 h-4 mr-2" /> Send Reply</>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setReplyingTo(null); setReplyMessage(''); }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2 mt-3">
                              {message.sender_email && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setReplyingTo(message)}
                                  className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                                >
                                  <Reply className="w-4 h-4 mr-2" /> Reply
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/campaign/${message.campaign_id}`)}
                                className="text-muted-foreground"
                              >
                                <ExternalLink className="w-4 h-4 mr-2" /> View Campaign
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mb-2">No messages yet</p>
                      <p className="text-sm text-muted-foreground">
                        When supporters contact you about your campaigns, their messages will appear here.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Backed Projects Tab */}
            <TabsContent value="backed">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Heart className="w-5 h-5 text-rose-400" />
                        Projects You've Backed
                      </CardTitle>
                      <CardDescription>
                        View all campaigns you've supported with donations
                      </CardDescription>
                    </div>
                    {backedProjects.total_projects_backed > 0 && (
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-400">{formatCurrency(backedProjects.total_amount_donated)}</p>
                        <p className="text-xs text-muted-foreground">Total contributed</p>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : backedProjects.backed_projects?.length > 0 ? (
                    <div className="space-y-4">
                      {/* Stats Summary */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="p-4 bg-secondary rounded-lg text-center">
                          <p className="text-2xl font-bold text-foreground">{backedProjects.total_projects_backed}</p>
                          <p className="text-xs text-muted-foreground">Projects Backed</p>
                        </div>
                        <div className="p-4 bg-secondary rounded-lg text-center">
                          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(backedProjects.total_amount_donated)}</p>
                          <p className="text-xs text-muted-foreground">Total Donated</p>
                        </div>
                        <div className="p-4 bg-secondary rounded-lg text-center">
                          <p className="text-2xl font-bold text-teal-400">{backedProjects.total_donations}</p>
                          <p className="text-xs text-muted-foreground">Total Donations</p>
                        </div>
                      </div>

                      {/* Backed Projects List */}
                      {backedProjects.backed_projects.map((item) => (
                        <div 
                          key={item.campaign.id} 
                          className="p-4 bg-secondary rounded-lg border border-border hover:border-emerald-500/50 transition-all cursor-pointer"
                          onClick={() => navigate(`/campaign/${item.campaign.id}`)}
                        >
                          <div className="flex items-start gap-4">
                            <img
                              src={item.campaign.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.campaign.title)}&size=128&background=0f172a&color=22d3ee`}
                              alt={item.campaign.title}
                              className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={item.campaign.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                                  {item.campaign.status}
                                </Badge>
                                {item.campaign.category && (
                                  <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 text-xs">
                                    {item.campaign.category}
                                  </Badge>
                                )}
                              </div>
                              <h4 className="font-semibold text-foreground mb-1 truncate">{item.campaign.title}</h4>
                              <p className="text-xs text-muted-foreground mb-2">
                                by {item.campaign.creator?.name || 'Unknown'}
                              </p>
                              <Progress 
                                value={Math.min((item.campaign.raised / item.campaign.goal) * 100, 100)} 
                                className="h-1.5" 
                              />
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-emerald-400">{formatCurrency(item.campaign.raised, item.campaign.currency)} raised</span>
                                <span className="text-muted-foreground">of {formatCurrency(item.campaign.goal, item.campaign.currency)}</span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="bg-rose-500/20 text-rose-400 px-3 py-1 rounded-full text-sm font-medium mb-2">
                                Your contribution: {formatCurrency(item.user_contribution.total_donated, item.campaign.currency)}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {item.user_contribution.donation_count} donation{item.user_contribution.donation_count > 1 ? 's' : ''}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Last: {new Date(item.user_contribution.last_donation).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Heart className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mb-4">You haven't backed any projects yet.</p>
                      <Link to="/discover">
                        <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                          <Heart className="w-4 h-4 mr-2" /> Discover Projects to Support
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="donations">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Donation History</CardTitle>
                    <Button variant="outline" onClick={handleExportDonations}>
                      <Download className="w-4 h-4 mr-2" /> Export CSV
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : myDonations.length > 0 ? (
                    <div className="space-y-4">
                      {myDonations.map((donation) => (
                        <div key={donation.id} className="flex items-center justify-between p-4 border-b border-border last:border-0">
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-rose-500/20 rounded-full flex items-center justify-center">
                              <Heart className="w-5 h-5 text-rose-400" />
                            </div>
                            <div>
                              <h4 className="font-medium text-foreground">{donation.campaign}</h4>
                              <p className="text-sm text-muted-foreground">{donation.date}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-foreground">{formatCurrency(donation.amount)}</p>
                            <Badge variant="outline" className="text-primary">{donation.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">You haven't made any donations yet.</p>
                      <Link to="/discover">
                        <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                          <Heart className="w-4 h-4 mr-2" /> Discover Campaigns
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* My Subscriptions Tab - For Donors */}
            <TabsContent value="subscriptions">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-violet-400" />
                    My Recurring Donations
                  </CardTitle>
                  <CardDescription>
                    Manage your monthly subscriptions to campaigns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {subscriptionsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                    </div>
                  ) : mySubscriptions.length === 0 ? (
                    <div className="text-center py-12">
                      <RefreshCw className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-muted-foreground">You don't have any active subscriptions yet.</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        When you set up a monthly donation to a campaign, it will appear here.
                      </p>
                      <Link to="/discover">
                        <Button className="mt-4" variant="outline">
                          Browse Campaigns
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {mySubscriptions.map((sub) => (
                        <div 
                          key={sub.id} 
                          className="border border-border rounded-lg p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-violet-400" />
                              </div>
                              <div>
                                <h4 className="font-medium text-foreground">
                                  {sub.campaign_title || 'Campaign Subscription'}
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  ${sub.amount}/month
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 text-xs">
                              <Badge 
                                variant={
                                  sub.status === 'active' && !sub.cancel_at_period_end ? 'default' : 
                                  sub.status === 'canceled' || sub.status === 'cancelled' ? 'destructive' : 
                                  (sub.cancel_at_period_end || sub.status === 'cancel_at_period_end') ? 'secondary' :
                                  'secondary'
                                }
                                className={
                                  sub.status === 'active' && !sub.cancel_at_period_end ? 'bg-emerald-500/20 text-emerald-400' : 
                                  (sub.cancel_at_period_end || sub.status === 'cancel_at_period_end') ? 'text-amber-500 border-amber-500' :
                                  ''
                                }
                              >
                                {sub.status === 'active' && !sub.cancel_at_period_end ? 'Active' : 
                                 (sub.cancel_at_period_end || sub.status === 'cancel_at_period_end') ? 'Cancelling' :
                                 sub.status === 'canceled' || sub.status === 'cancelled' ? 'Cancelled' : 
                                 sub.status === 'past_due' ? 'Past Due' :
                                 sub.status}
                              </Badge>
                              {(sub.cancel_at_period_end || sub.status === 'cancel_at_period_end') && sub.current_period_end && (
                                <span className="text-amber-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Ends: {new Date(sub.current_period_end).toLocaleDateString()}
                                </span>
                              )}
                              {sub.next_billing_date && (
                                <span className="text-muted-foreground flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Next billing: {new Date(sub.next_billing_date).toLocaleDateString()}
                                </span>
                              )}
                              {sub.created_at && (
                                <span className="text-muted-foreground">
                                  Started: {new Date(sub.created_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {sub.campaign_id && (
                              <Link to={`/campaign/${sub.campaign_id}`}>
                                <Button size="sm" variant="outline" className="text-xs">
                                  <ExternalLink className="w-3 h-3 mr-1" /> View Campaign
                                </Button>
                              </Link>
                            )}
                            {sub.status === 'active' && !sub.cancel_at_period_end && sub.status !== 'cancel_at_period_end' && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                className="text-xs"
                                onClick={() => handleCancelSubscription(sub.id, sub.stripe_subscription_id)}
                                disabled={cancellingSubscription === sub.id}
                              >
                                {cancellingSubscription === sub.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                ) : (
                                  <X className="w-3 h-3 mr-1" />
                                )}
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* My Messages Tab - For Donors */}
            <TabsContent value="my-messages">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-400" />
                    My Messages & Replies
                  </CardTitle>
                  <CardDescription>
                    View messages you've sent to campaign creators and their replies
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {tabLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : myConversations.length > 0 ? (
                    <div className="space-y-4">
                      {myConversations.map((conv) => (
                        <div 
                          key={conv.id} 
                          className="p-4 rounded-lg border bg-secondary border-border"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-medium text-foreground">
                                To: {conv.recipient_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Campaign: {conv.campaign_title}
                              </p>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {new Date(conv.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          
                          {/* Original Message */}
                          <div className="mb-3 p-3 bg-background rounded-lg border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Your message:</p>
                            <p className="text-sm font-medium text-foreground">{conv.subject}</p>
                            <p className="text-sm text-muted-foreground mt-1">{conv.message}</p>
                          </div>
                          
                          {/* Reply if exists */}
                          {conv.reply ? (
                            <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                              <p className="text-xs text-emerald-400 mb-1 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Reply from {conv.recipient_name}:
                              </p>
                              <p className="text-sm text-foreground">{conv.reply.message}</p>
                              <p className="text-xs text-muted-foreground mt-2">
                                {new Date(conv.reply.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">
                              Awaiting reply from creator...
                            </p>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            className="mt-3 text-muted-foreground"
                            onClick={() => navigate(`/campaign/${conv.campaign_id}`)}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" /> View Campaign
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground mb-2">No messages sent yet</p>
                      <p className="text-sm text-muted-foreground">
                        When you contact campaign creators, your conversations will appear here.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Disputes Tab */}
            <TabsContent value="disputes">
              <UserDisputesSection />
            </TabsContent>

            <TabsContent value="invoices">
              <UserInvoicesSection />
            </TabsContent>

            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Notifications</CardTitle>
                    <div className="flex items-center gap-2">
                      {selectedNotifications.length > 0 && (
                        <>
                          <span className="text-sm text-muted-foreground">{selectedNotifications.length} selected</span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-400 border-red-500/30 hover:bg-red-500/20"
                            disabled={bulkActionLoading}
                            onClick={handleBulkDeleteNotifications}
                          >
                            {bulkActionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                            Delete Selected
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setSelectedNotifications([])}>
                            Clear
                          </Button>
                        </>
                      )}
                      <Button variant="outline" size="sm" onClick={markAllNotificationsRead}>
                        Mark All Read
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {notifications.length > 0 ? (
                    <div className="space-y-4">
                      {/* Select All */}
                      <div className="flex items-center gap-3 pb-3 border-b border-border">
                        <Checkbox
                          checked={selectedNotifications.length === notifications.length && notifications.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedNotifications(notifications.map(n => n.id));
                            else setSelectedNotifications([]);
                          }}
                        />
                        <span className="text-sm text-muted-foreground">Select All ({notifications.length})</span>
                      </div>
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`flex items-start gap-3 ${
                            !notification.read ? 'bg-emerald-500/5 border border-emerald-500/20 rounded-lg' : ''
                          } ${selectedNotifications.includes(notification.id) ? 'ring-2 ring-emerald-500/30 rounded-lg' : ''}`}
                        >
                          <div className="pt-4 pl-2" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedNotifications.includes(notification.id)}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedNotifications(prev => [...prev, notification.id]);
                                else setSelectedNotifications(prev => prev.filter(id => id !== notification.id));
                              }}
                            />
                          </div>
                          <div className="flex-1 flex items-start space-x-4 p-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              notification.type === 'donation' ? 'bg-emerald-500/20' :
                              notification.type === 'milestone' ? 'bg-teal-500/20' :
                              notification.type === 'verification' ? 'bg-emerald-500/20' : 'bg-secondary'
                            }`}>
                              {notification.type === 'donation' && <DollarSign className="w-5 h-5 text-emerald-400" />}
                              {notification.type === 'milestone' && <TrendingUp className="w-5 h-5 text-teal-400" />}
                              {notification.type === 'update' && <MessageCircle className="w-5 h-5 text-blue-400" />}
                              {notification.type === 'verification' && <BadgeCheck className="w-5 h-5 text-emerald-400" />}
                            </div>
                            <div className="flex-1">
                              <p className="text-foreground">{notification.message}</p>
                              <p className="text-sm text-muted-foreground mt-1">{notification.time}</p>
                            </div>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No notifications yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Team Pages Tab (Creator) */}
            <TabsContent value="team-pages" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-500" />
                    Team Fundraisers
                  </CardTitle>
                  <CardDescription>
                    Manage team fundraisers supporting your campaigns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Campaign selector for team pages */}
                  {activeCampaigns.length > 0 ? (
                    <div className="space-y-6">
                      <div className="grid gap-4">
                        {/* Show all campaigns with option to enable team fundraising */}
                        {myCampaigns.filter(c => c.status === 'active' || c.status === 'pending_verification').length > 0 ? (
                          myCampaigns.filter(c => c.status === 'active' || c.status === 'pending_verification').map(campaign => (
                            <div key={campaign.id} className="border border-border rounded-xl p-4">
                              <div className="flex items-center justify-between gap-3 mb-4">
                                <div className="flex items-center gap-3">
                                  <img src={campaign.image} alt={campaign.title} className="w-12 h-12 rounded-lg object-cover" />
                                  <div>
                                    <h3 className="font-semibold text-foreground">{campaign.title}</h3>
                                    <p className="text-sm text-muted-foreground">
                                      {campaign.team_fundraising_enabled ? (
                                        <span className="text-emerald-400">✓ Team fundraising enabled</span>
                                      ) : (
                                        <span className="text-muted-foreground">Team fundraising disabled</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant={campaign.team_fundraising_enabled ? "outline" : "default"}
                                  size="sm"
                                  className={campaign.team_fundraising_enabled 
                                    ? "border-red-500/30 text-red-400 hover:bg-red-500/10" 
                                    : "bg-emerald-600 hover:bg-emerald-700"
                                  }
                                  disabled={actionLoading[`team-${campaign.id}`]}
                                  data-testid={`team-toggle-btn-${campaign.id}`}
                                  onClick={async () => {
                                    // Prevent double-click
                                    if (actionLoading[`team-${campaign.id}`]) return;
                                    
                                    setActionLoading(prev => ({ ...prev, [`team-${campaign.id}`]: true }));
                                    try {
                                      const response = await apiClient.post(`/campaigns/${campaign.id}/team-fundraising/toggle`);
                                      console.log('Team fundraising toggle response:', response.data);
                                      
                                      // Toggle succeeded - update local state immediately
                                      const newEnabled = response.data.team_fundraising_enabled;
                                      setMyCampaigns(prev => prev.map(c => 
                                        c.id === campaign.id ? { ...c, team_fundraising_enabled: newEnabled } : c
                                      ));
                                      setActiveCampaigns(prev => prev.map(c => 
                                        c.id === campaign.id ? { ...c, team_fundraising_enabled: newEnabled } : c
                                      ));
                                    } catch (err) {
                                      console.error('Failed to toggle team fundraising:', err);
                                      // Better error messages
                                      const status = err.response?.status;
                                      const detail = err.response?.data?.detail;
                                      
                                      if (status === 429) {
                                        const retryAfter = err.response?.data?.retry_after || 60;
                                        alert(`Too many requests. Please wait ${retryAfter} seconds and try again.`);
                                      } else if (status === 403) {
                                        alert('You are not authorized to modify this campaign.');
                                      } else if (status === 404) {
                                        alert('Campaign not found.');
                                      } else {
                                        console.error('Toggle error details:', { status, detail, error: err });
                                        alert(detail || `Failed to toggle team fundraising (${status || 'network error'}). Please try again.`);
                                      }
                                    } finally {
                                      setActionLoading(prev => ({ ...prev, [`team-${campaign.id}`]: false }));
                                    }
                                  }}
                                >
                                  {actionLoading[`team-${campaign.id}`] ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : campaign.team_fundraising_enabled ? (
                                    'Disable'
                                  ) : (
                                    <>
                                      <Users className="w-4 h-4 mr-1" /> Enable
                                    </>
                                  )}
                                </Button>
                              </div>
                              {campaign.team_fundraising_enabled && (
                                <CreatorTeamPagesSection campaignId={campaign.id} />
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8">
                            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">No Active Campaigns</h3>
                            <p className="text-muted-foreground mb-4">
                              Create a campaign first to enable team fundraising.
                            </p>
                            <Link to="/create">
                              <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
                                <PlusCircle className="w-4 h-4 mr-2" /> Create Campaign
                              </Button>
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No active campaigns found</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* My Team Pages Tab (Donor) */}
            <TabsContent value="my-team-pages" className="space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <MyTeamPagesSection />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Referrals Tab — hidden when REFERRALS_ENABLED=false */}
            {REFERRALS_ENABLED && (
              <TabsContent value="referrals" className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <ReferralDashboardSection />
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Collaboration Invitations Tab */}
            <TabsContent value="invitations" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-500" />
                    Collaboration Invitations
                  </CardTitle>
                  <CardDescription>
                    Campaign owners have invited you to collaborate. Accept to help manage their campaigns.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {invitesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                      <span className="ml-2 text-muted-foreground">Loading invitations...</span>
                    </div>
                  ) : collaborationInvites.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-1">No Pending Invitations</h3>
                      <p className="text-sm text-muted-foreground">
                        When campaign owners invite you to collaborate, invitations will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {collaborationInvites.map((invite) => (
                        <div
                          key={invite.campaign_id}
                          className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 border border-border rounded-lg bg-card/50 hover:bg-card transition-colors"
                        >
                          <img
                            src={invite.campaign_image || '/placeholder-campaign.png'}
                            alt={invite.campaign_title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-foreground truncate">{invite.campaign_title}</h4>
                            <p className="text-sm text-muted-foreground">
                              Invited by {invite.owner?.name || 'Campaign Owner'}
                            </p>
                            {invite.invited_at && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Invited {new Date(invite.invited_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Button
                              size="sm"
                              onClick={() => handleRespondToInvite(invite.campaign_id, 'accept')}
                              disabled={respondingToInvite === invite.campaign_id}
                              className="flex-1 sm:flex-none bg-emerald-500 hover:bg-emerald-600"
                            >
                              {respondingToInvite === invite.campaign_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Accept
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRespondToInvite(invite.campaign_id, 'decline')}
                              disabled={respondingToInvite === invite.campaign_id}
                              className="flex-1 sm:flex-none border-red-500/50 text-red-500 hover:bg-red-500/10"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-6 pt-6 border-t border-border">
                    <h4 className="font-medium text-foreground mb-2">What are Collaborators?</h4>
                    <p className="text-sm text-muted-foreground">
                      Collaborators are verified users who help manage a campaign. As a collaborator, you can help update campaign information, respond to donors, and share campaign progress. This builds trust with donors and shows transparency.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payouts / Settings Tab */}
            <TabsContent value="payouts" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-emerald-500" />
                    Payout Settings
                  </CardTitle>
                  <CardDescription>
                    Configure how you receive donations from your campaigns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <StripeConnectSetup />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
      
      {/* Proof Upload Dialog - Moved outside CampaignListItem to fix scope issue */}
      <Dialog open={showProofUploadDialog} onOpenChange={setShowProofUploadDialog}>
        <DialogContent className="bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Upload Milestone Proof</DialogTitle>
            <DialogDescription>
              Upload proof of completion for: {selectedMilestone?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-muted-foreground">Select File</label>
              <Input
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                className="bg-card border-border mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Accepted formats: Images, PDF, Documents (Max 10MB)
              </p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Description (Optional)</label>
              <Textarea
                value={proofDescription}
                onChange={(e) => setProofDescription(e.target.value)}
                placeholder="Describe the proof you're uploading..."
                className="bg-card border-border mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProofUploadDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadProof}
              disabled={!proofFile || uploadingProof}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {uploadingProof ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Upload Proof'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Campaign List Item Component
const CampaignListItem = ({ 
  campaign, 
  formatCurrency, 
  isProject = false, 
  showTypeLabel = false,
  showCampaignId = false,
  showActions = false,
  showRestoreAction = false,
  onPause,
  onResume,
  onDelete,
  onRestore,
  actionLoading
}) => {
  const navigate = useNavigate();
  
  const handleCardClick = (e) => {
    // Don't navigate if clicking on a button, link, or badge
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[role="menu"]') || e.target.closest('[data-badge]')) {
      return;
    }
    // Don't navigate for deleted campaigns - user must use Restore button
    if (campaign.status === 'deleted') {
      return;
    }
    navigate(`/campaign/${campaign.id}`);
  };

  const progressPercent = campaign.goal > 0 ? Math.min((campaign.raised / campaign.goal) * 100, 100) : 0;
  
  // Determine if this is a project or campaign based on type field
  const itemType = campaign.type || (isProject ? 'project' : 'campaign');
  const isProjectType = itemType === 'project';
  
  // Determine if campaign can be paused (only active campaigns)
  const canPause = campaign.status === 'active' && !actionLoading;
  const canResume = campaign.status === 'inactive' && !actionLoading;
  const canDelete = ['active', 'inactive', 'pending_verification'].includes(campaign.status) && !actionLoading;

  return (
    <div 
      className={`p-4 bg-secondary rounded-lg border cursor-pointer hover:bg-secondary/80 transition-all ${
        campaign.status === 'deleted' ? 'border-red-500/30 opacity-75' : 
        campaign.status === 'inactive' ? 'border-yellow-500/30' : 
        'border-border hover:border-emerald-500/50'
      }`}
      onClick={handleCardClick}
    >
      <div className="flex items-start gap-4 mb-4">
        <img
          src={campaign.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.title)}&size=128&background=0f172a&color=22d3ee&bold=true`}
          alt={campaign.title}
          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          {/* Status & Type Badges */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {/* Collaborator Badge - Show when user is collaborator but not owner */}
            {campaign.is_collaborator && !campaign.is_owner && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                <Users className="w-3 h-3 mr-1" /> Collaborator
              </Badge>
            )}
            {/* Type Badge (Campaign or Project) - Always show if showTypeLabel is true */}
            {showTypeLabel && (
              isProjectType ? (
                <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30">
                  <Rocket className="w-3 h-3 mr-1" /> Project
                </Badge>
              ) : (
                <Badge className="bg-pink-500/20 text-pink-400 border-pink-500/30">
                  <Heart className="w-3 h-3 mr-1" /> Campaign
                </Badge>
              )
            )}
            {campaign.status === 'active' && <Badge className="bg-green-500/20 text-green-400">Active</Badge>}
            {campaign.status === 'completed' && <Badge className="bg-blue-500/20 text-blue-400">Completed</Badge>}
            {campaign.status === 'inactive' && <Badge className="bg-yellow-500/20 text-yellow-400">Paused</Badge>}
            {campaign.status === 'deleted' && <Badge className="bg-red-500/20 text-red-400">Deleted</Badge>}
            {campaign.status === 'pending_verification' && <Badge className="bg-yellow-500/20 text-yellow-400">Pending</Badge>}
            {campaign.category && <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 text-xs" data-badge="category">{campaign.category}</Badge>}
            {/* Campaign ID Badge */}
            {showCampaignId && campaign.id && (
              <Badge 
                variant="outline" 
                className="text-muted-foreground border-border/30 text-xs font-mono cursor-pointer hover:bg-slate-500/10"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(campaign.id);
                  // Show a brief tooltip or notification
                  const badge = e.target;
                  const originalText = badge.textContent;
                  badge.textContent = 'Copied!';
                  setTimeout(() => {
                    badge.textContent = originalText;
                  }, 1000);
                }}
                data-testid={`campaign-id-badge-${campaign.id}`}
                title="Click to copy Campaign ID"
              >
                ID: {campaign.id.substring(0, 8)}...
              </Badge>
            )}
          </div>
          
          {/* Title */}
          <h4 className="font-semibold text-foreground hover:text-emerald-400 transition-colors mb-1">{campaign.title}</h4>
          
          {/* Description Preview */}
          {campaign.description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{campaign.description}</p>
          )}
          
          {/* Stats Row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              <span className="text-foreground font-medium">{formatCurrency(campaign.raised, campaign.currency)}</span> / {formatCurrency(campaign.goal, campaign.currency)}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {campaign.donors || 0} donors
            </span>
            {campaign.daysLeft > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {campaign.daysLeft} days left
              </span>
            )}
            {campaign.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Created: {new Date(campaign.created_at).toLocaleDateString()}
              </span>
            )}
            {campaign.location && (
              <span className="flex items-center gap-1">
                📍 {campaign.location}
              </span>
            )}
          </div>
          
          {/* Milestones indicator */}
          {campaign.milestones && campaign.milestones.length > 0 && (
            <div className="mt-2 flex items-center gap-1 text-xs text-emerald-400">
              <Target className="w-3 h-3" />
              <span>{campaign.milestones.filter(m => m.status === 'completed').length}/{campaign.milestones.length} milestones</span>
            </div>
          )}
          
          {/* Collaborators indicator */}
          {campaign.collaborators && campaign.collaborators.length > 0 && (
            <div className="mt-1 flex items-center gap-1 text-xs text-emerald-400">
              <Shield className="w-3 h-3" />
              <span>{campaign.collaborators.length} collaborator{campaign.collaborators.length > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        
        {/* Progress Circle */}
        <div className="flex flex-col items-center">
          <div className="relative w-14 h-14">
            <svg className="w-14 h-14 transform -rotate-90">
              <circle cx="28" cy="28" r="24" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-slate-700" />
              <circle 
                cx="28" cy="28" r="24" 
                stroke="currentColor" 
                strokeWidth="4" 
                fill="transparent" 
                strokeDasharray={`${progressPercent * 1.51} 151`}
                className="text-emerald-500" 
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-foreground">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <span className="text-xs text-muted-foreground mt-1">funded</span>
        </div>
      </div>
      
      {/* Progress Bar */}
      <Progress value={progressPercent} className="h-2 mb-4" />
      
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 items-center" onClick={(e) => e.stopPropagation()}>
        {/* Restore button for deleted campaigns */}
        {showRestoreAction && onRestore && (
          <Button 
            size="sm" 
            className="bg-emerald-500 hover:bg-emerald-600"
            onClick={onRestore}
            disabled={actionLoading === 'restore'}
          >
            {actionLoading === 'restore' ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4 mr-1" />
            )}
            Restore Campaign
          </Button>
        )}
        
        {/* Resume button for paused campaigns */}
        {showActions && canResume && onResume && (
          <Button 
            size="sm" 
            className="bg-emerald-500 hover:bg-emerald-600"
            onClick={onResume}
            disabled={actionLoading === 'resume'}
          >
            {actionLoading === 'resume' ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-1" />
            )}
            Resume
          </Button>
        )}
        
        {/* Regular action buttons (only for non-deleted campaigns) */}
        {campaign.status !== 'deleted' && !showRestoreAction && (
          <>
            <Link to={`/campaign/${campaign.id}`}>
              <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:opacity-90">
                <Eye className="w-4 h-4 mr-1" /> View
              </Button>
            </Link>
            <Link to={`/campaign/${campaign.id}/analytics`}>
              <Button size="sm" variant="outline" className="hover:bg-emerald-500/10 hover:border-emerald-500/50">
                <TrendingUp className="w-4 h-4 mr-1 text-emerald-400" /> Analytics
              </Button>
            </Link>
            {campaign.status === 'active' && (
              <Link to="/verification">
                <Button size="sm" variant="outline" className="hover:bg-emerald-500/10 hover:border-emerald-500/50">
                  <Wallet className="w-4 h-4 mr-1 text-emerald-400" /> Withdraw
                </Button>
              </Link>
            )}
            <Link to={`/campaign/${campaign.id}/update`}>
              <Button size="sm" variant="outline" className="hover:bg-teal-500/10 hover:border-teal-500/50">
                <MessageCircle className="w-4 h-4 mr-1 text-teal-400" /> Update
              </Button>
            </Link>
            <Link to={`/campaign/${campaign.id}/edit`}>
              <Button size="sm" variant="outline">
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
            </Link>
            <Button 
              size="sm" 
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(`${window.location.origin}/campaign/${campaign.id}`);
                alert('Campaign link copied!');
              }}
            >
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
            
            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canPause && onPause && (
                  <DropdownMenuItem 
                    onClick={onPause}
                    className="text-yellow-500"
                    disabled={actionLoading === 'pause'}
                  >
                    {actionLoading === 'pause' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Pause className="w-4 h-4 mr-2" />
                    )}
                    Pause Campaign
                  </DropdownMenuItem>
                )}
                {canResume && onResume && (
                  <DropdownMenuItem 
                    onClick={onResume}
                    className="text-green-500"
                    disabled={actionLoading === 'resume'}
                  >
                    {actionLoading === 'resume' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Resume Campaign
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {canDelete && onDelete && (
                  <DropdownMenuItem 
                    onClick={onDelete}
                    className="text-red-500"
                    disabled={actionLoading === 'delete'}
                  >
                    {actionLoading === 'delete' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete Campaign
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
      
    </div>
  );
};

export default DashboardPage;
